import { PDFArray, PDFDict, PDFDocument, PDFDropdown, PDFForm, PDFName, PDFOptionList, PDFRadioGroup, PDFRef, PDFTextField } from 'pdf-lib'

export class PdfFormService {
    public async fillAndSerialize(pdfBytes: ArrayBuffer, values: Record<string, string | boolean>): Promise<Uint8Array> {
        const pdfDoc = await PDFDocument.load(pdfBytes)
        this.ensureFillableAcroForm(pdfDoc)
        const form = pdfDoc.getForm()

        for (const [name, value] of Object.entries(values)) {
            try {
                if (typeof value === 'boolean') {
                    const checkbox = form.getCheckBox(name)
                    value ? checkbox.check() : checkbox.uncheck()
                } else if (typeof value === 'string') {
                    this.setFieldValue(form, name, value)
                }
            } catch (e) {
                console.warn(`Could not fill field "${name}":`, e)
            }
        }

        return pdfDoc.save({ updateFieldAppearances: true })
    }

    /** Support PDFs without AcroForm */
    private ensureFillableAcroForm(pdfDoc: PDFDocument): void {
        const context = pdfDoc.context

        // Inspect the catalog directly - calling getForm() here would create and
        // cache an empty /AcroForm, which then shadows the one we rebuild below.
        const existingAcroForm = pdfDoc.catalog.lookupMaybe(PDFName.of('AcroForm'), PDFDict)
        const existingFields = existingAcroForm?.lookupMaybe(PDFName.of('Fields'), PDFArray)
        if (existingFields && existingFields.size() > 0) {
            return
        }

        const fieldRefs: PDFRef[] = []
        const seenFields = new Set<string>()

        pdfDoc.getPages().forEach(page => {
            const annotations = page.node.Annots()
            if (annotations) {
                annotations.asArray().forEach(ref => {
                    const widget = ref instanceof PDFRef ? context.lookup(ref) : undefined
                    if (ref instanceof PDFRef && widget instanceof PDFDict && widget.get(PDFName.of('Subtype'))?.toString() === '/Widget') {
                        let fieldRef = ref
                        let fieldDict: PDFDict = widget
                        let hasFieldType = !!fieldDict.get(PDFName.of('FT'))
                        let parent = fieldDict.get(PDFName.of('Parent'))
                        let depth = 0
                        while (parent instanceof PDFRef && depth < 50) {
                            const parentDict = context.lookup(parent)
                            if (!(parentDict instanceof PDFDict)) {
                                break
                            }
                            fieldRef = parent
                            fieldDict = parentDict
                            hasFieldType = hasFieldType || !!fieldDict.get(PDFName.of('FT'))
                            parent = fieldDict.get(PDFName.of('Parent'))
                            depth += 1
                        }

                        const key = fieldRef.toString()
                        if (hasFieldType && !seenFields.has(key)) {
                            seenFields.add(key)
                            fieldRefs.push(fieldRef)
                        }
                    }
                })
            }
        })

        if (fieldRefs.length === 0) {
            return
        }

        const fields = PDFArray.withContext(context)
        fieldRefs.forEach(ref => fields.push(ref))

        const acroForm = context.obj({ NeedAppearances: true }) as PDFDict
        acroForm.set(PDFName.of('Fields'), fields)
        pdfDoc.catalog.set(PDFName.of('AcroForm'), context.register(acroForm))
    }

    private setFieldValue(form: PDFForm, name: string, value: string): void {
        const field = form.getField(name)

        if (field instanceof PDFTextField) {
            form.getTextField(name).setText(value)
        } else if (field instanceof PDFDropdown) {
            form.getDropdown(name).select(value)
        } else if (field instanceof PDFRadioGroup) {
            form.getRadioGroup(name).select(value)
        } else if (field instanceof PDFOptionList) {
            form.getOptionList(name).select(value)
        }
    }

}
